import React, { useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { SwiperSlide } from 'swiper/react';
import qs from 'query-string';

// Components
import Slider from '../Slider';
import Spinner from '../Spinner';
import DisneyBannerVideoSlide from './partials/DisneyBannerVideoSlide';

// State
import { applyStock } from '../../state/modules/stock/actions';

// Router
import { getRoute } from '../../entry/routes';

// Utils
import { CITY_THEME_DISNEY, isThemeActive } from '../../utils/cityTheme';

// Styles
import styles from './MainSlider.styl';

const cx = classNames.bind(styles);

const BANNER_AUTOPLAY_DELAY = 8000;

export default function MainSlider(props) {
  const { className, homePageInnerRef } = props;

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const stockCode = useSelector(state => state.stock.data?.code);
  const list = useSelector(state => state.banner.list);
  const isBannersLoaded = useSelector(state => state.banner.isLoaded);
  const userCity = useSelector(state => state.city.userCity);
  const { isPhone, isTablet } = useSelector(state => state.responsive);

  const { bannerId } = qs.parse(location.search);
  const bannersList = list.filter(banner => banner.type === 'slider');
  const isMobile = isPhone || isTablet;
  const showDisneyVideo = isThemeActive(userCity, CITY_THEME_DISNEY);
  const slideCount = bannersList.length + (showDisneyVideo ? 1 : 0);

  const bannerInitialIndex = bannersList.findIndex(banner => banner.id === Number(bannerId));
  const initialSlide =
    bannerInitialIndex === -1
      ? 0
      : showDisneyVideo
        ? bannerInitialIndex + 1
        : bannerInitialIndex;

  const swiperRef = useRef(null);

  const syncAutoplayForSlide = useCallback(
    swiper => {
      if (!swiper?.autoplay || slideCount <= 1) {
        return;
      }

      if (showDisneyVideo && swiper.realIndex === 0) {
        swiper.autoplay.stop();
        return;
      }

      swiper.params.autoplay.delay = BANNER_AUTOPLAY_DELAY;
      swiper.autoplay.start();
    },
    [showDisneyVideo, slideCount],
  );

  const handleSwiperInit = useCallback(
    swiper => {
      swiperRef.current = swiper;
      syncAutoplayForSlide(swiper);
    },
    [syncAutoplayForSlide],
  );

  const handleSlideChange = useCallback(
    swiper => {
      syncAutoplayForSlide(swiper);
    },
    [syncAutoplayForSlide],
  );

  const handleVideoEnded = useCallback(() => {
    const swiper = swiperRef.current;

    if (swiper) {
      swiper.slideNext();
    }
  }, []);

  const handleVideoClick = useCallback(() => {
    history.push(getRoute('toystory5'));
  }, [history]);

  // применяем промокод
  const processStock = newStockCode => {
    if (stockCode === newStockCode) {
      return;
    }

    if (!isMobile && homePageInnerRef.current) {
      homePageInnerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    dispatch(applyStock({ stock_code: newStockCode }));
  };

  // переходим по линку после нажатия
  const processLink = href => {
    if (href) {
      // RegExp check if url is absolute
      const urlRegex = new RegExp('^(?:[a-z]+:)?//', 'i');

      if (urlRegex.test(href.trim())) {
        return Object.assign(document.createElement('a'), {
          href,
          target: '_blank',
        }).click();
      }

      return history.push(getRoute(href));
    }

    return false;
  };

  const handleOnClick = banner => {
    if (banner.link) {
      return processLink(banner.link);
    }

    if (banner.stock) {
      return processStock(banner.stock);
    }

    return () => {};
  };

  const swiperParams = {
    slidesPerView: isMobile ? 1 : 1.75,
    spaceBetween: 20,
    centeredSlides: true,
    loop: slideCount > 1,
    autoplay:
      slideCount > 1
        ? {
            delay: BANNER_AUTOPLAY_DELAY,
            disableOnInteraction: false,
          }
        : false,
    navigation: true,
    initialSlide,
    preloadImages: false,
    lazy: { loadPrevNext: true, loadOnTransitionStart: true },
    watchSlidesVisibility: true,
  };

  return (
    <div className={cx('MainSlider', className)}>
      {isBannersLoaded && slideCount > 0 ? (
        <Slider
          swiperParams={swiperParams}
          isOutsidePagination
          onSwiper={handleSwiperInit}
          onSlideChange={handleSlideChange}
        >
          {showDisneyVideo && (
            <SwiperSlide
              key="disney-banner-video"
              className={cx('MainSlider__item')}
              tag="button"
              type="button"
              onClick={handleVideoClick}
              onKeyDown={e => e.key === 'Enter' && handleVideoClick()}
            >
              {({ isActive }) => (
                <DisneyBannerVideoSlide
                  isActive={isActive}
                  isPhone={isPhone}
                  onEnded={handleVideoEnded}
                />
              )}
            </SwiperSlide>
          )}
          {bannersList.map(banner => (
            <SwiperSlide
              key={banner.image_full}
              className={cx('MainSlider__item')}
              onClick={() => handleOnClick(banner)}
              onKeyDown={e => e.key === 'Enter' && handleOnClick(banner)}
              tag="button"
            >
              {({ isActive }) => (
                <picture>
                  <source
                    data-srcset={isPhone ? banner.image_mobile_webp : banner.image_full_webp}
                    type="image/webp"
                  />
                  <img
                    className={`swiper-lazy ${cx('MainSlider__item-img', {
                      'MainSlider__item-img_active': isActive,
                    })}`}
                    data-src={isPhone ? banner.image_mobile : banner.image_full}
                    data-srcset={isPhone ? banner.image_mobile : banner.image_full}
                    alt="banner"
                  />
                </picture>
              )}
            </SwiperSlide>
          ))}
        </Slider>
      ) : (
        <div className={cx('MainSlider__spinner')}>
          <Spinner />
        </div>
      )}
    </div>
  );
}

MainSlider.defaultProps = {
  className: '',
};

MainSlider.propTypes = {
  className: PropTypes.string,
  homePageInnerRef: PropTypes.object.isRequired,
};
